import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./lib/i18n";
import { TopNav } from "./components/layout/TopNav";
import { InkWireBar } from "./components/layout/InkWireBar";
import { Footer } from "./components/layout/Footer";
import { MobileNav } from "./components/layout/MobileNav";

import Home from "./pages/Home";
import Articles from "./pages/Articles";
import ArticleDetail from "./pages/ArticleDetail";
import Shorts from "./pages/Shorts";
import InkWire from "./pages/InkWire";
import PtiPlay from "./pages/PtiPlay";
import MemoryMatch from "./pages/MemoryMatch";
import GamePlayer from "./pages/GamePlayer";
import Advertise from "./pages/Advertise";
import PublishSubmit from "./pages/PublishSubmit";
import MyPublishing from "./pages/MyPublishing";
import Creators from "./pages/Creators";
import CreatorProfile from "./pages/CreatorProfile";
import About from "./pages/About";
import Auth from "./pages/Auth";
import CreatorDashboard from "./pages/CreatorDashboard";
import AdvertiserDashboard from "./pages/AdvertiserDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import Daily3Desk from "./pages/Daily3Desk";
import PaymentResult from "./pages/PaymentResult";
import Legal from "./pages/Legal";
import RoleSelect from "./pages/RoleSelect";
import Unsubscribe from "./pages/Unsubscribe";

function Layout() {
  return (
    <div className="min-h-screen flex flex-col">
      <TopNav />
      <InkWireBar />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <MobileNav />
    </div>
  );
}

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="pti-container py-24 text-center text-gray-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AuthCallback() {
  const { loginWithGoogleSession } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const m = window.location.hash.match(/session_id=([^&]+)/);
    const sid = m ? m[1] : null;
    (async () => {
      try {
        if (!sid) throw new Error("no session");
        const { user, newUser } = await loginWithGoogleSession(sid);
        window.history.replaceState(null, "", window.location.pathname);
        toast.success("Signed in with Google.");
        if (newUser) return navigate("/welcome", { replace: true });
        navigate(user.role === "admin" ? "/admin" : user.role === "advertiser" ? "/advertiser" : user.role === "creator" ? "/dashboard" : "/", { replace: true });
      } catch {
        window.history.replaceState(null, "", window.location.pathname);
        toast.error("Google sign-in failed. Please try again.");
        navigate("/login", { replace: true });
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return <div className="pti-container py-24 text-center text-gray-400" data-testid="auth-callback">Signing you in…</div>;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/articles" element={<Articles />} />
        <Route path="/article/:slug" element={<ArticleDetail />} />
        <Route path="/shorts" element={<Shorts />} />
        <Route path="/inkwire" element={<InkWire />} />
        <Route path="/pti-play" element={<PtiPlay />} />
        <Route path="/pti-play/memory-match" element={<MemoryMatch />} />
        <Route path="/pti-play/:slug" element={<GamePlayer />} />
        <Route path="/advertise" element={<Advertise />} />
        <Route path="/publish" element={<PublishSubmit />} />
        <Route path="/my-publishing" element={<Protected roles={["reader", "creator", "advertiser", "editor", "moderator", "admin"]}><MyPublishing /></Protected>} />
        <Route path="/creators" element={<Creators />} />
        <Route path="/creators/:username" element={<CreatorProfile />} />
        <Route path="/about" element={<About />} />
        <Route path="/login" element={<Auth mode="login" />} />
        <Route path="/register" element={<Auth mode="register" />} />
        <Route path="/legal/:page" element={<Legal />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/welcome" element={<RoleSelect />} />
        <Route path="/payment/success" element={<PaymentResult />} />
        <Route path="/payment/cancel" element={<PaymentResult />} />
        <Route path="/dashboard" element={<Protected roles={["creator", "admin"]}><CreatorDashboard /></Protected>} />
        <Route path="/advertiser" element={<Protected roles={["advertiser", "admin"]}><AdvertiserDashboard /></Protected>} />
        <Route path="/admin" element={<Protected roles={["admin"]}><AdminDashboard /></Protected>} />
        <Route path="/admin/daily3" element={<Protected roles={["admin"]}><Daily3Desk /></Protected>} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors />
          <AppRouter />
        </BrowserRouter>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
